/**
 * RegisterBrowser: the Points step for a Modbus device (browse kind
 * `register-range`). Populates a candidate register list from
 * any of these sources and lets the operator tick which registers to map:
 *
 *   1. Read the live device through the gateway chosen in the wizard, relayed
 *      through the cloud: a v2 gateway (config schema 2 in its birth) pages
 *      through any register space with `browse` (cursor {space, start, count},
 *      following `next`); an older gateway keeps `gateway.sniff()`, holding
 *      registers 0..63, exactly as before
 *   2. Load a known equipment map: things.equipmentTemplates() (real Modbus
 *      register maps from Versatile's fleet)
 *   3. Load from a VSync instrument: VSyncInstrumentPicker (typical map, or paste
 *      the device's exact config.txt)
 *   4. Add a row by hand (space + 0-based register, checked as the platform
 *      checks a Modbus address), or import a point map (CSV / JSON, previewed
 *      row by row before it is applied)
 *
 * All of them normalise to the same {@link SniffedRegister} row shape and
 * render in the shared widget Table with checkboxes (select-all in the header).
 * Every row is editable (lib/iot/register-map): type, word order (only for a
 * value spanning 2+ registers), name, unit and scale. Retyping a row to a 2- or
 * 4-register value re-decodes its sampled value from the words already read
 * (17541, 40960 as float32 ABCD = 1069.0) and greys the rows it covers ("part
 * of register 0's value"), which then cannot be ticked. The ticked rows seed
 * the Map step's tag rows with their type, word order and scale; the map can be
 * exported for the next device of the same kind. The device's address, port and
 * unit come from the Protocol step's connection form.
 */

import { useId, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ListTree, Loader2, Plus, ScanLine, Trash2, TriangleAlert } from "lucide-react";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/lib/ui/card";
import { Input } from "@/lib/ui/input";
import { Label } from "@/lib/ui/label";
import { NativeSelect } from "@/lib/ui/native-select";
import { useToast } from "@/lib/ui/toast";
import { cn } from "@/lib/ui/cn";
import { Table, type ColumnDef } from "@/lib/widgets";
import { things, isLiveApi } from "@/lib/api";
import { errorCodeOf } from "@/lib/api/error";
import { modbusEntryToRegister, registerKey } from "@/lib/iot/points";
import { parseRegisterRef } from "@/lib/iot/instrument-body";
import { pointIdentity } from "@/lib/iot/point-map-io";
import {
  MODBUS_WORD_TYPES,
  REGISTER_SPACES,
  addRegister,
  consumedRegisters,
  defaultRegisterName,
  editRegister,
  isBitSpace,
  mappedRegisters,
  mergeRegisterRecords,
  registerNameProblems,
  registerToRecord,
  registerType,
  registerWords,
  removeRegister,
  sampledValue,
  withRawWord,
  type ModbusValueType,
  type NewRegister,
  type RegisterEdit,
  type RegisterMapState,
} from "@/lib/iot/register-map";
import { VSyncInstrumentPicker } from "@/components/iot/VSyncInstrumentPicker";
import type { TagRow } from "@/components/iot/ThingTagRows";
import {
  gateway,
  scanErrorMessage,
  type DiscoveredDevice,
  type SniffedRegister,
} from "@/lib/gateway-client";
import { modbusDataType } from "./commit";
import {
  ImportPreview,
  KnownMapPicker,
  MapFileButtons,
  MapFooter,
  MapFormatHelp,
  ScaleInput,
  WordOrderPicker,
  type MapPreview,
} from "./PointMapParts";
import { DeviceErrorText } from "@/components/connect/DeviceErrorText";

type Space = NonNullable<SniffedRegister["space"]>;
const SPACES: Array<{ value: Space; label: string }> = [
  { value: "holding", label: "Holding registers" },
  { value: "input", label: "Input registers" },
  { value: "coil", label: "Coils" },
  { value: "discrete", label: "Discrete inputs" },
];
/** Registers per browse page (the sniff's 64, so both read the same first page). */
const PAGE = 64;

/** The Type select's label for a type ("double" is float64, as the device manuals say). */
const TYPE_LABEL: Record<ModbusValueType, string> = {
  bool: "bool",
  int16: "int16",
  uint16: "uint16",
  int32: "int32",
  uint32: "uint32",
  float32: "float32",
  float64: "double",
};

export interface RegisterBrowserProps {
  device: DiscoveredDevice;
  /** The Modbus driver (modbus-tcp / modbus-rtu) and the link from the Protocol step. */
  driverId: string;
  link: Record<string, unknown>;
  /** The gateway answers `browse` (its birth advertises config schema v2); else the old sniff. */
  canBrowse: boolean;
  /** The gateway chosen on the Discover step; the sniff runs on it. */
  gatewayId: string | null;
  gatewayName: string;
  registers: SniffedRegister[];
  onRegisters: (registers: SniffedRegister[]) => void;
  selectedIds: Set<string>;
  onSelectionChange: (next: Set<string>) => void;
  /** "Bring in selected": go on to the Map step with the ticked rows. */
  onContinue?: () => void;
}

/** "holding 0" / "input 4": a row's address as the rest of the step names it. */
function where(r: Pick<SniffedRegister, "space" | "address">): string {
  return `${r.space ?? "holding"} ${r.address}`;
}

export function RegisterBrowser({
  device,
  driverId,
  link,
  canBrowse,
  gatewayId,
  gatewayName,
  registers,
  onRegisters,
  selectedIds,
  onSelectionChange,
  onContinue,
}: RegisterBrowserProps) {
  const toast = useToast();
  const [sniffing, setSniffing] = useState(false);
  // Why the last sniff produced no rows (a failure, or a device with nothing readable).
  const [notice, setNotice] = useState<{ tone: "error" | "info"; text: string } | null>(null);
  const [templateId, setTemplateId] = useState("");
  const [space, setSpace] = useState<Space>("holding");
  const [nextCursor, setNextCursor] = useState<Record<string, unknown> | null>(null);
  // The gateway refused browse (older firmware for this driver): read the old way.
  const [browseRefused, setBrowseRefused] = useState(false);
  const [adding, setAdding] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [preview, setPreview] = useState<MapPreview | null>(null);
  const useBrowse = canBrowse && !browseRefused;
  // The target, as the connection form has it (the scan's, unless edited there).
  const host = typeof link.host === "string" && link.host ? link.host : device.ip;
  const port = typeof link.port === "number" ? link.port : device.port;
  const unitId = typeof link.unit_id === "number" ? link.unit_id : device.unit_id;
  const target = host || "the device";
  const canSniff = isLiveApi && !!gatewayId && !sniffing && (useBrowse || !!host);

  const templatesQuery = useQuery({
    queryKey: ["connect", "equipment-templates"],
    queryFn: () => things.equipmentTemplates(),
    enabled: isLiveApi,
    staleTime: 5 * 60_000,
  });
  const templates = templatesQuery.data?.templates ?? [];

  // Replace the candidate list AND auto-select every row (operator deselects).
  // Each read row keeps its raw word, so a retype can re-decode it.
  const setAll = (read: SniffedRegister[]) => {
    const regs = read.map(withRawWord);
    onRegisters(regs);
    onSelectionChange(new Set(regs.map(registerKey)));
  };

  /** Apply an edit's result, or say why it was refused. */
  const apply = (next: RegisterMapState): boolean => {
    if (next.error) {
      toast.push("error", next.error);
      return false;
    }
    onRegisters(next.registers);
    onSelectionChange(next.selected);
    return true;
  };
  const edit = (key: string, e: RegisterEdit) => apply(editRegister(registers, selectedIds, key, e));

  const sniffOld = async () => {
    if (!gatewayId || !isLiveApi || !host) return;
    setSniffing(true);
    setNotice(null);
    try {
      const res = await gateway.sniff(gatewayId, host, {
        unit_id: unitId,
        port,
      });
      setAll(res.registers);
      if (res.registers.length > 0) {
        toast.push("ok", `Read ${res.registers.length} registers from ${host}.`);
      } else {
        setNotice({
          tone: "info",
          text: `${host} answered, but none of holding registers 0 to 63 could be read. Check the unit id, or load a known register map instead.`,
        });
      }
    } catch (e) {
      setNotice({
        tone: "error",
        text: scanErrorMessage(e, { op: "sniff", gatewayName, deviceIp: port ? `${host}:${port}` : host }),
      });
    } finally {
      setSniffing(false);
    }
  };

  /** One browse page (v2 gateways): the first page of `space`, or the next one. */
  const browsePage = async (more: boolean) => {
    if (!gatewayId || !canSniff) return;
    setSniffing(true);
    setNotice(null);
    const cursor = more && nextCursor ? nextCursor : { space, start: 0, count: PAGE };
    let refused = false;
    try {
      const res = await gateway.browse(gatewayId, { driver: driverId, link, cursor });
      const regs = res.entries
        .map(modbusEntryToRegister)
        .filter((r): r is SniffedRegister => r !== null)
        .map(withRawWord);
      setNextCursor(res.next ?? null);
      if (more) {
        const have = new Set(registers.map(registerKey));
        const fresh = regs.filter((r) => !have.has(registerKey(r)));
        onRegisters([...registers, ...fresh]);
        onSelectionChange(new Set([...selectedIds, ...fresh.map(registerKey)]));
      } else {
        setAll(regs);
      }
      if (regs.length === 0 && !more) {
        setNotice({
          tone: "info",
          text: `${target} answered, but nothing in the first ${PAGE} ${space} addresses could be read. Try another register space, check the unit id, or load a known register map instead.`,
        });
      } else if (regs.length > 0) {
        const what = space === "coil" || space === "discrete" ? "bits" : "registers";
        toast.push("ok", `Read ${regs.length} ${what} from ${target}.`);
      }
    } catch (e) {
      if (errorCodeOf(e) === "gateway_cannot_browse") {
        refused = true;
      } else {
        setNotice({
          tone: "error",
          text: scanErrorMessage(e, { op: "browse", gatewayName, deviceIp: port ? `${host}:${port}` : host }),
        });
      }
    } finally {
      setSniffing(false);
    }
    if (refused) {
      // Firmware without v2 browse for this driver: fall back to the old register read.
      setBrowseRefused(true);
      await sniffOld();
    }
  };

  const sniff = () => (useBrowse ? browsePage(false) : sniffOld());

  const loadTemplate = () => {
    const t = templates.find((x) => x.equipment_type === templateId);
    if (!t) return;
    setNotice(null);
    // Template registers are legacy text ("4624" is holding 623): rows hold the 0-based register.
    const regs: SniffedRegister[] = t.params.map((p) => ({
      address: parseRegisterRef(p.register) ?? 0,
      data_type: p.data_type,
      reg_count: p.reg_count ?? (p.data_type === "int16" ? 1 : 2),
      value: "—",
      guessed_name: p.metric,
      ...(p.kind && p.kind !== "holding" ? { space: p.kind } : {}),
      ...(p.word_order ? { word_order: p.word_order } : {}),
      ...(p.scale !== undefined && p.scale !== 1 ? { scale: p.scale } : {}),
    }));
    setAll(regs);
    toast.push(
      "info",
      `${t.display_name}: ${regs.length} registers loaded — verify the addresses against this unit.`,
    );
  };

  const loadVsyncRows = (rows: TagRow[]) => {
    setNotice(null);
    const regs: SniffedRegister[] = rows.map((r) => ({
      address: parseRegisterRef(r.address) ?? 0,
      data_type: modbusDataType(r),
      reg_count: r.reg_count ?? 1,
      value: "—",
      guessed_name: r.name || "param",
      unit: r.unit || undefined,
    }));
    setAll(regs);
  };

  const consumed = consumedRegisters(registers);
  const nameProblems = registerNameProblems(registers, selectedIds);
  const mapped = mappedRegisters(registers, selectedIds);
  const listed = new Set(registers.map((r) => pointIdentity(driverId, registerToRecord(r).address)));
  const isCovered = (r: SniffedRegister) => consumed.has(registerKey(r));

  const columns: ColumnDef<SniffedRegister>[] = [
    {
      id: "address",
      header: "Register",
      accessor: (r) => r.address,
      cell: (r) => (
        <span className="font-mono text-on-surface">
          {r.space && r.space !== "holding" ? <span className="text-on-surface-muted">{r.space} </span> : null}
          {r.address}
        </span>
      ),
      width: "w-28",
    },
    {
      id: "type",
      header: "Type",
      accessor: (r) => registerType(r),
      cell: (r) => {
        const by = consumed.get(registerKey(r));
        if (by) {
          // The rest of a covered row is this one note.
          const whose = by.space && by.space !== "holding" ? `${by.space} register ${by.address}` : `register ${by.address}`;
          return <span className="whitespace-nowrap text-xs italic text-on-surface-muted">part of {whose}&apos;s value</span>;
        }
        if (isBitSpace(r.space)) {
          return (
            <Badge variant="secondary" className="font-mono text-[10px]">
              bool
            </Badge>
          );
        }
        return (
          <NativeSelect
            aria-label={`Type of ${where(r)}`}
            value={registerType(r)}
            onChange={(e) => edit(registerKey(r), { type: e.target.value as ModbusValueType })}
            className="h-7 w-24 px-1 font-mono text-xs"
          >
            {MODBUS_WORD_TYPES.map((t) => (
              <option key={t} value={t}>
                {TYPE_LABEL[t]}
              </option>
            ))}
          </NativeSelect>
        );
      },
    },
    {
      id: "word_order",
      header: "Word order",
      unsortable: true,
      cell: (r) =>
        !isCovered(r) && registerWords(r) >= 2 ? (
          <WordOrderPicker
            label={`Word order of ${where(r)}`}
            value={r.word_order ?? "big"}
            onChange={(w) => edit(registerKey(r), { word_order: w })}
          />
        ) : null,
    },
    {
      id: "name",
      header: "Name",
      accessor: (r) => r.guessed_name,
      cell: (r) => {
        if (isCovered(r)) return null;
        const problem = nameProblems.get(registerKey(r));
        return (
          <span className="flex flex-col gap-0.5">
            <Input
              aria-label={`Name of ${where(r)}`}
              value={r.guessed_name}
              onChange={(e) => edit(registerKey(r), { name: e.target.value })}
              aria-invalid={!!problem}
              className="h-7 w-40 font-mono text-xs"
            />
            {problem && <span className="text-[11px] text-danger">{problem}</span>}
          </span>
        );
      },
    },
    {
      id: "unit",
      header: "Unit",
      accessor: (r) => r.unit ?? "",
      cell: (r) =>
        isCovered(r) ? null : (
          <Input
            aria-label={`Unit of ${where(r)}`}
            value={r.unit ?? ""}
            onChange={(e) => edit(registerKey(r), { unit: e.target.value })}
            placeholder="kg"
            className="h-7 w-16 text-xs"
          />
        ),
    },
    {
      id: "scale",
      header: "Scale",
      unsortable: true,
      cell: (r) =>
        isCovered(r) ? null : (
          <ScaleInput
            label={`Scale of ${where(r)}`}
            value={r.scale}
            onCommit={(scale) => edit(registerKey(r), { scale })}
          />
        ),
    },
    {
      id: "value",
      header: "Sampled",
      accessor: (r) => (typeof r.value === "number" ? r.value : 0),
      align: "right",
      cell: (r) => {
        if (isCovered(r)) return <span className="font-mono text-on-surface-muted">{String(r.value)}</span>;
        const v = sampledValue(registers, r);
        return (
          <span className="font-mono text-on-surface" title={v.raw ? `decoded ${v.raw} x scale ${r.scale}` : undefined}>
            {v.text}
            {r.unit ? <span className="text-on-surface-muted"> {r.unit}</span> : null}
          </span>
        );
      },
    },
    {
      id: "remove",
      header: "",
      unsortable: true,
      cell: (r) => (
        <button
          type="button"
          aria-label={`Remove ${where(r)}`}
          title="Remove this row"
          onClick={() => apply(removeRegister(registers, selectedIds, registerKey(r)))}
          className="rounded p-1 text-on-surface-muted hover:text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Trash2 className="size-3.5" />
        </button>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ListTree className="size-5 text-brand" />
            Register map — {port ? `${host}:${port}` : host}
          </CardTitle>
          <CardDescription>
            Read the device&apos;s registers through the gateway (or load a known map, or add them by hand), set each
            row&apos;s type, and tick the ones to bring in.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-3">
            {useBrowse && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="connect-space">Register space</Label>
                <select
                  id="connect-space"
                  value={space}
                  onChange={(e) => {
                    setSpace(e.target.value as Space);
                    setNextCursor(null);
                  }}
                  disabled={sniffing}
                  className="h-9 rounded-md border border-border bg-surface px-2 text-sm text-on-surface focus:border-brand focus:outline-none disabled:opacity-50"
                >
                  {SPACES.map((sp) => (
                    <option key={sp.value} value={sp.value}>
                      {sp.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <Button onClick={() => void sniff()} disabled={!canSniff}>
              {sniffing ? <Loader2 className="animate-spin" /> : <ScanLine />}
              {sniffing ? "Reading…" : useBrowse ? "Read registers" : "Sniff device"}
            </Button>
            {useBrowse && nextCursor && registers.length > 0 && (
              <Button variant="outline" onClick={() => void browsePage(true)} disabled={!canSniff}>
                Read the next {PAGE}
              </Button>
            )}
            <Button variant="outline" aria-expanded={adding} onClick={() => setAdding((v) => !v)}>
              <Plus /> Add row
            </Button>
            <MapFileButtons
              driverId={driverId}
              records={mapped.map(registerToRecord)}
              deviceLabel={port ? `${host}:${port}` : (host ?? "device")}
              helpOpen={helpOpen}
              onHelp={setHelpOpen}
              onPreview={setPreview}
              onError={(msg) => toast.push("error", msg)}
            />

            <div className="flex min-w-56 flex-col gap-1.5">
              <Label htmlFor="connect-template">…or load a known register map</Label>
              <div className="flex items-center gap-2">
                <select
                  id="connect-template"
                  value={templateId}
                  onChange={(e) => setTemplateId(e.target.value)}
                  disabled={!isLiveApi || templates.length === 0}
                  className="h-9 flex-1 rounded-md border border-border bg-surface px-2 text-sm text-on-surface focus:border-brand focus:outline-none disabled:opacity-50"
                >
                  <option value="">
                    {isLiveApi
                      ? templatesQuery.isLoading
                        ? "Loading templates…"
                        : "Select equipment model…"
                      : "Templates need the live API"}
                  </option>
                  {templates.map((t) => (
                    <option key={t.equipment_type} value={t.equipment_type}>
                      {t.display_name} ({t.params.length})
                    </option>
                  ))}
                </select>
                <Button variant="outline" onClick={loadTemplate} disabled={!templateId}>
                  Load map
                </Button>
              </div>
            </div>
          </div>

          <KnownMapPicker
            driverId={driverId}
            deviceProduct={device.identity?.product}
            onPreview={setPreview}
            onError={(msg) => toast.push("error", msg)}
          />

          {adding && (
            <ManualRegisterForm
              onAdd={(input) => apply(addRegister(registers, selectedIds, input))}
              onClose={() => setAdding(false)}
            />
          )}
          {helpOpen && <MapFormatHelp driverId={driverId} />}
          {preview && (
            <ImportPreview
              preview={preview}
              driverId={driverId}
              listed={listed}
              onCancel={() => setPreview(null)}
              onApply={(records, mode) => {
                const res = mergeRegisterRecords(registers, selectedIds, records, mode);
                if (apply(res)) {
                  setPreview(null);
                  toast.push(
                    "ok",
                    mode === "replace"
                      ? `Loaded ${res.added} registers from ${preview.file}.`
                      : `${preview.file}: ${res.added} added, ${res.updated} updated.`,
                  );
                }
              }}
            />
          )}

          {sniffing ? (
            <p role="status" className="flex items-center gap-2 text-xs text-on-surface-muted">
              <Loader2 className="size-3.5 shrink-0 animate-spin text-brand" />
              {useBrowse
                ? `Reading ${space} addresses from ${target}`
                : `Reading holding registers 0 to 63 from ${target}`}
              {unitId > 0 ? ` (unit ${unitId})` : ""} through {gatewayName}. This can take up to 40
              seconds.
            </p>
          ) : notice ? (
            <div
              role={notice.tone === "error" ? "alert" : "status"}
              className={cn(
                "flex items-start gap-2 rounded-md border p-3 text-xs",
                notice.tone === "error"
                  ? "border-danger/40 bg-danger/5"
                  : "border-warning/40 bg-warning/5",
              )}
            >
              <TriangleAlert
                className={cn(
                  "mt-0.5 size-3.5 shrink-0",
                  notice.tone === "error" ? "text-danger" : "text-warning",
                )}
              />
              {notice.tone === "error" ? (
                <DeviceErrorText message={notice.text} />
              ) : (
                <span className="text-on-surface-muted">{notice.text}</span>
              )}
            </div>
          ) : !isLiveApi ? (
            <p className="text-xs text-on-surface-muted">
              Reading registers needs the live API. Use a VSync instrument&apos;s register map below
              instead.
            </p>
          ) : !gatewayId ? (
            <p className="text-xs text-on-surface-muted">
              Pick a gateway on the Discover step to read this device&apos;s registers.
            </p>
          ) : !useBrowse && !host ? (
            <p className="text-xs text-on-surface-muted">
              Reading a serial device&apos;s registers needs a gateway on GatewayPro v0.3 or later. Load
              a known register map, or add the registers with Add row.
            </p>
          ) : browseRefused ? (
            <p className="text-xs text-on-surface-muted">
              {gatewayName} answers only the older register read, so holding registers 0 to 63 are
              read.
            </p>
          ) : null}

          <VSyncInstrumentPicker onRows={loadVsyncRows} />

          <Table
            getRowId={registerKey}
            columns={columns}
            data={registers}
            state={sniffing ? { kind: "loading" } : { kind: "ok" }}
            selectable
            selectedIds={selectedIds}
            onSelectionChange={(next) => onSelectionChange(new Set([...next].filter((k) => !consumed.has(k))))}
            isRowDisabled={isCovered}
            hideSearch={registers.length <= 8}
            hidePagination
            emptyTitle="No registers yet"
            emptyDescription="Read the device, load a known map, add a row by hand or import a map."
          />

          {registers.length > 0 && <MapFooter count={mapped.length} onContinue={onContinue} />}
        </CardContent>
      </Card>
    </div>
  );
}

/** Add row: register space + 0-based register + type, and optionally name / unit / scale. */
function ManualRegisterForm({
  onAdd,
  onClose,
}: {
  onAdd: (input: NewRegister) => boolean;
  onClose: () => void;
}) {
  const id = useId();
  const [space, setSpace] = useState<Space>("holding");
  const [register, setRegister] = useState("");
  const [type, setType] = useState<ModbusValueType>("int16");
  const [name, setName] = useState("");
  const [unit, setUnit] = useState("");
  const [scale, setScale] = useState("");
  const scaleN = Number(scale);
  const scaleBad = scale.trim() !== "" && (!Number.isFinite(scaleN) || scaleN === 0);
  const bit = isBitSpace(space);
  const regN = Number(register.trim());
  const suggested =
    register.trim() && Number.isInteger(regN) && regN >= 0 ? defaultRegisterName(space, regN) : "weight";

  const add = () => {
    if (scaleBad) return;
    const ok = onAdd({
      space,
      register,
      type: bit ? "bool" : type,
      name,
      unit,
      scale: scale.trim() === "" ? undefined : scaleN,
    });
    if (ok) {
      setRegister("");
      setName("");
    }
  };

  const field = "flex flex-col gap-1";
  const lab = "text-[11px] font-medium text-on-surface";
  return (
    <section
      aria-label="Add a register"
      className="flex flex-col gap-2 rounded-lg border border-border bg-surface-elevated p-3"
    >
      <div className="flex flex-wrap items-end gap-3">
        <div className={field}>
          <label htmlFor={`${id}-space`} className={lab}>
            Space
          </label>
          <NativeSelect
            id={`${id}-space`}
            value={space}
            onChange={(e) => setSpace(e.target.value as Space)}
            className="h-8 w-40 text-xs"
          >
            {REGISTER_SPACES.map((sp) => (
              <option key={sp} value={sp}>
                {SPACES.find((x) => x.value === sp)?.label ?? sp}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className={field}>
          <label htmlFor={`${id}-reg`} className={lab}>
            Register <span className="text-danger">*</span>
          </label>
          <Input
            id={`${id}-reg`}
            inputMode="numeric"
            value={register}
            onChange={(e) => setRegister(e.target.value)}
            placeholder="0"
            title="0-based wire address: holding register 40001 is 0"
            className="h-8 w-24 font-mono text-xs"
          />
        </div>
        <div className={field}>
          <label htmlFor={`${id}-type`} className={lab}>
            Type
          </label>
          <NativeSelect
            id={`${id}-type`}
            value={bit ? "bool" : type}
            disabled={bit}
            onChange={(e) => setType(e.target.value as ModbusValueType)}
            className="h-8 w-28 font-mono text-xs"
          >
            {(bit ? (["bool"] as ModbusValueType[]) : MODBUS_WORD_TYPES).map((t) => (
              <option key={t} value={t}>
                {TYPE_LABEL[t]}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className={field}>
          <label htmlFor={`${id}-name`} className={lab}>
            Name
          </label>
          <Input
            id={`${id}-name`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={suggested}
            className="h-8 w-40 font-mono text-xs"
          />
        </div>
        <div className={field}>
          <label htmlFor={`${id}-unit`} className={lab}>
            Unit
          </label>
          <Input
            id={`${id}-unit`}
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            placeholder="kg"
            className="h-8 w-20 text-xs"
          />
        </div>
        <div className={field}>
          <label htmlFor={`${id}-scale`} className={lab}>
            Scale
          </label>
          <Input
            id={`${id}-scale`}
            inputMode="decimal"
            value={scale}
            onChange={(e) => setScale(e.target.value)}
            placeholder="1"
            aria-invalid={scaleBad}
            className="h-8 w-20 font-mono text-xs"
          />
        </div>
        <Button size="sm" onClick={add}>
          <Plus /> Add register
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Done
        </Button>
      </div>
      {scaleBad && <p className="text-[11px] text-danger">Scale: a number other than 0 (empty means 1).</p>}
      <p className="text-[11px] text-on-surface-muted">
        A 32-bit type reads this register and the next; a 64-bit one the next three.
      </p>
    </section>
  );
}
